import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import uploadFileToBlob from '@/lib/azure-blob';
import { UserDto } from '@cloudcost/shared-types/users';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';

const StyledModal = styled(Modal)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CircleUploadButton = styled(Box)(({ theme }) => ({
  width: 120,
  height: 120,
  borderRadius: '50%',
  border: '1px dashed #C6CFDC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#7A8497',
  backgroundColor: '#FCFDFF',
  position: 'relative',
  overflow: 'hidden',
  transition: 'background-color .12s, border-color .12s',
  '&:hover': {
    backgroundColor: '#F2F7FD',
    borderColor: '#9FC2F0',
  },
}));

const UserProfileSection = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(5),
}));

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  userValue: any;
  setUserValue: (value: any) => void;
  currenciesList: any[];
  items: UserDto[];
  canEditField: (fieldType: 'basic' | 'admin' | 'limited') => boolean;
  onCreateUser: () => void;
  onDeleteUser: () => void;
  errorMessage: string;
  onClearError?: () => void;
  alertFirstName: boolean;
  alertLastName: boolean;
  alertUsername: boolean;
  alertEmail: boolean;
  alertPassword: boolean;
  alertDefaultCurrency: boolean;
  validateField: (field: string, value: any) => boolean;
  validate: (value: any) => void;
  currentUser: any;
  hasAdminAccess: boolean;
  loading?: boolean;
  isFormValid?: boolean;
}

export default function UserDialog({
  open,
  onClose,
  userValue,
  setUserValue,
  currenciesList,
  items,
  canEditField,
  onCreateUser,
  onDeleteUser,
  errorMessage,
  onClearError,
  alertFirstName,
  alertLastName,
  alertUsername,
  alertEmail,
  alertPassword,
  alertDefaultCurrency,
  validateField,
  validate,
  currentUser,
  hasAdminAccess,
  loading = false,
  isFormValid = false,
}: UserDialogProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [fileSelected, setFileSelected] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(Math.random().toString(36));
  const [confirmation, setConfirmation] = useState(false);
  const [deletePasswordHashConfirmation, setDeletePasswordHashConfirmation] =
    useState(false);
  const [validationErrorOpen, setValidationErrorOpen] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!open && previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [open, previewUrl]);

  const onFileChange = (event: any) => {
    const file = event.target.files[0];
    setFileSelected(file);

    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setUserValue({
        ...userValue,
        photo_path: file.name,
      });
    }
  };

  const onFileUpload = async () => {
    if (fileSelected) {
      const uploaded = await uploadFileToBlob(fileSelected);
      if (uploaded?.url) {
        setUserValue({ ...userValue, photo_path: uploaded.url });
      }
      setFileSelected(null);

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setInputKey(Math.random().toString(36));
    }
  };

  const handleCreateUser = () => {
    const existingUser = items.find(
      (user: any) =>
        user.username === userValue.username && user.id !== userValue.id,
    );

    if (existingUser && userValue.username) {
      setValidationError(
        'Username already exists in the system. Please choose a different username.',
      );
      setValidationErrorOpen(true);
      return;
    }

    const existingEmail = items.find(
      (user: any) => user.email === userValue.email && user.id !== userValue.id,
    );

    if (existingEmail && userValue.email) {
      setValidationError(
        'Email already exists in the system. Please choose a different email.',
      );
      setValidationErrorOpen(true);
      return;
    }

    onFileUpload();
    onCreateUser();
  };

  const handleDeleteConfirmation = () => {
    setConfirmation(true);
  };

  const closeConfirmation = () => {
    setConfirmation(false);
  };

  const handleDelete = () => {
    onDeleteUser();
    closeConfirmation();
  };

  const canDeletePasswordHash =
    hasAdminAccess && !userValue?.new && !userValue?.is_admin;

  const handleDeletePasswordHash = async () => {
    try {
      await fetchWrapper.post(urls.DevOps.deletePasswordHash(userValue.id), {});
      setUserValue({ ...userValue, password: '', local_login_enabled: false });
      setDeletePasswordHashConfirmation(false);
      setValidationError('Password login has been disabled for this user.');
      setValidationErrorOpen(true);
    } catch (e: any) {
      setDeletePasswordHashConfirmation(false);
      setValidationError(e?.message ?? 'Failed to disable password login');
      setValidationErrorOpen(true);
    }
  };

  const roleDescriptions = {
    admin: 'Managing CloudCost Suite configuration for the organisation',
    technical:
      'Technical owner responsible for the cloud account / tenant or managing cloud resources',
    sponsor: 'Global budget owner and supervisor of total cloud spend',
    business: 'Business owner responsible for a unit or system budget',
  };

  const roleConfig = [
    {
      key: 'is_technical',
      label: 'Technical',
      description: roleDescriptions.technical,
    },
    {
      key: 'is_business',
      label: 'Business',
      description: roleDescriptions.business,
    },
    {
      key: 'is_sponsor',
      label: 'Sponsor',
      description: roleDescriptions.sponsor,
      validation: (checked: boolean) => {
        if (checked) {
          const existingSponsor = items.find(
            (user: any) => user.is_sponsor && user.id !== userValue.id,
          );
          if (existingSponsor) {
            setValidationError(
              'There can be only one sponsor in the system. Please remove the current sponsor role from another user first.',
            );
            setValidationErrorOpen(true);
            return false;
          }
        }
        return true;
      },
    },
    {
      key: 'is_admin',
      label: 'Admin',
      description: roleDescriptions.admin,
    },
  ];

  return (
    <>
      <StyledModal
        open={open}
        onClose={onClose}
        closeAfterTransition
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}>
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95vw', sm: 750, md: 850 },
              maxWidth: '95vw',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              boxShadow: '0 24px 60px rgba(16,35,58,.22)',
              p: 4,
              borderRadius: 'var(--cc-radius)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ mb: 3, flexShrink: 0 }}>
              {userValue.new ? 'Add New User' : 'Edit User'}
            </Typography>

            <Grid container spacing={2} sx={{ flex: 1, overflowY: 'auto' }}>
              {/* PHOTO UPLOAD */}
              <Grid item xs={12} md={3}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  User Details
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <CircleUploadButton component="label">
                    {previewUrl || userValue.photo_path ? (
                      <Avatar
                        src={previewUrl || userValue.photo_path}
                        sx={{ width: 116, height: 116 }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ textAlign: 'center' }}>
                        Upload Photo
                      </Typography>
                    )}
                    <input
                      type="file"
                      onChange={onFileChange}
                      key={inputKey}
                      hidden
                      accept="image/*"
                    />
                  </CircleUploadButton>
                </Box>
              </Grid>

              {/* USER DETAILS */}
              <Grid item xs={12} md={4} style={{ paddingLeft: 0 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  &nbsp;
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    id="firstname"
                    label="First name"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={userValue.firstName ?? ''}
                    disabled={!canEditField('basic')}
                    error={alertFirstName}
                    helperText={alertFirstName ? 'First name is required' : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserValue({ ...userValue, firstName: value });
                      validateField('firstName', value);
                    }}
                  />

                  <TextField
                    id="lastname"
                    label="Last name"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={userValue.lastName ?? ''}
                    disabled={!canEditField('basic')}
                    error={alertLastName}
                    helperText={alertLastName ? 'Last name is required' : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserValue({ ...userValue, lastName: value });
                      validateField('lastName', value);
                    }}
                  />

                  <TextField
                    id="email"
                    label="Email"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={userValue.email ?? ''}
                    disabled={!canEditField('basic')}
                    error={alertEmail}
                    helperText={
                      alertEmail ? 'Please enter a valid email address' : ''
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserValue({ ...userValue, email: value });
                      validateField('email', value);
                    }}
                  />

                  <TextField
                    id="username"
                    label="Username"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={userValue.username ?? ''}
                    disabled={!canEditField('basic')}
                    error={alertUsername}
                    helperText={
                      alertUsername
                        ? 'Username is required and must be at least 3 characters'
                        : ''
                    }
                    onChange={(e) => {
                      const newUsername = e.target.value;
                      setUserValue({ ...userValue, username: newUsername });
                      validateField('username', newUsername);
                    }}
                  />

                  {canEditField('limited') && (
                    <>
                      <TextField
                        id="password"
                        label={
                          userValue.new
                            ? 'Password'
                            : 'Password (leave blank to keep current)'
                        }
                        size="small"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        fullWidth
                        value={userValue.password ?? ''}
                        error={alertPassword}
                        helperText={
                          alertPassword
                            ? userValue.new
                              ? 'Password is required for new users and must be at least 6 characters with lowercase, uppercase, number and special character'
                              : 'Password must be at least 6 characters with lowercase, uppercase, number and special character'
                            : ''
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          setUserValue({ ...userValue, password: value });
                          validate(value);
                          validateField('password', value);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end">
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}

                  <Autocomplete
                    disablePortal
                    id="combo-box-currencies"
                    options={currenciesList}
                    disabled={!canEditField('limited')}
                    value={userValue.default_currency ?? ''}
                    getOptionLabel={(option: any) => option.currency ?? option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Default Currency"
                        variant="outlined"
                        error={alertDefaultCurrency}
                        helperText={
                          alertDefaultCurrency
                            ? 'Default currency is required'
                            : ''
                        }
                      />
                    )}
                    onChange={(_event, value) => {
                      if (value) {
                        const currencyValue = value.currency ?? value;
                        setUserValue({
                          ...userValue,
                          default_currency: currencyValue,
                        });
                        validateField('default_currency', currencyValue);
                      }
                    }}
                  />
                </Box>
              </Grid>

              {/* USER PROFILE */}
              <Grid item xs={12} md={5}>
                <Typography
                  variant="h6"
                  sx={{ mb: 1, ml: 5 }}>
                  User Profile
                </Typography>

                <UserProfileSection>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {roleConfig.map((role) => (
                      <Box key={role.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={userValue[role.key] ?? false}
                              disabled={!canEditField('admin')}
                              size="small"
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (
                                  role.validation &&
                                  !role.validation(checked)
                                ) {
                                  return;
                                }
                                setUserValue({
                                  ...userValue,
                                  [role.key]: checked,
                                });
                              }}
                            />
                          }
                          label={role.label}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                          }}>
                          {role.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </UserProfileSection>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            {canEditField('limited') && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 3,
                  justifyContent: 'flex-end',
                }}>
                {hasAdminAccess &&
                  !userValue.new &&
                  currentUser?.id !== userValue.id && (
                    <Button
                      variant="contained"
                      onClick={handleDeleteConfirmation}
                      color="error"
                      size="small"
                      disabled={loading}>
                      Delete User
                    </Button>
                  )}

                {canDeletePasswordHash && (
                  <Button
                    variant="outlined"
                    onClick={() => setDeletePasswordHashConfirmation(true)}
                    color="warning"
                    size="small"
                    disabled={loading}>
                    Disable password login
                  </Button>
                )}

                <Button
                  variant="outlined"
                  onClick={onClose}
                  color="inherit"
                  size="small"
                  disabled={loading}>
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleCreateUser}
                  color="primary"
                  size="small"
                  disabled={loading || !isFormValid}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : undefined
                  }>
                  {userValue.new ? 'Create User' : 'Update'}
                </Button>
              </Box>
            )}

            {/* Loading Overlay */}
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Fade>
      </StyledModal>

      {/* Error Dialog */}
      <Dialog
        open={validationErrorOpen || !!errorMessage}
        onClose={() => setValidationErrorOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description">
        <DialogTitle id="error-dialog-title">
          {errorMessage ? 'Error' : 'Validation Error'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {errorMessage || validationError}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setValidationErrorOpen(false);
              if (errorMessage && onClearError) {
                onClearError();
              }
            }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Modal open={confirmation} onClose={closeConfirmation}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: '0 24px 60px rgba(16,35,58,.22)',
            p: 4,
            borderRadius: 'var(--cc-radius)',
          }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Are you sure you want to delete this user?
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            This action cannot be undone. All user data and history will be
            permanently deleted.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={closeConfirmation}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Disable Local Login Confirmation Dialog */}
      <Modal
        open={deletePasswordHashConfirmation}
        onClose={() => setDeletePasswordHashConfirmation(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420,
            bgcolor: 'background.paper',
            boxShadow: '0 24px 60px rgba(16,35,58,.22)',
            p: 4,
            borderRadius: 'var(--cc-radius)',
          }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Disable password login?
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            This removes the user&apos;s stored password hash and disables local
            password sign-in for this account. This action cannot be undone by
            the user.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setDeletePasswordHashConfirmation(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeletePasswordHash}
              color="warning"
              variant="contained">
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
